import type { Slide } from '@features/powerpoint';
import type { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import type { BuildPresentationProps } from '../../../utils/build-presentation';
import { campaignAnalyticsTitleSlideV2 } from './campaign-analytics-title-slide-v2';
import { audienceProfileChartSlideV2 } from './audience-profile/audience-profile-chart-slide-v2';
import { demographicProfileChartSlideV2 } from './demographic-profile/demographic-profile-chart-slide-v2';
import { stateAndDmaVisitsTableSlideV2 } from './state-and-dma-visits-slide-v2';
import { brandLocationsTableSlideV2 } from './brand-locations-slide-v2';
import { proximityToHomeChartSlideV2 } from './plain-chart-slides/proximity-to-home-chart-slide-v2';
import { timeToFirstVisitChartSlideV2 } from './plain-chart-slides/time-to-first-visit-chart-slide-v2';
import { visitsByTimeOfDayChartSlideV2 } from './plain-chart-slides/visits-by-time-of-day-chart-slide-v2';
import { visitsByDayOfWeekChartSlideV2 } from './plain-chart-slides/visits-by-day-of-week-chart-slide-v2';

export type CampaignAnalyticsSlidesV2Props = {
  slideMaster: string;
  campaignAnalyticsReport: CampaignAnalyticsReport;
};

const campaignAnalyticsTitleSlideFromProps = (
  props: BuildPresentationProps
): Slide[] =>
  Object.values(props.customFields.includedSlides.campaignAnalytics).some(
    Boolean
  )
    ? [campaignAnalyticsTitleSlideV2()]
    : [];

const audienceProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.audienceProfile &&
  props.campaignAnalyticsReport
    ? audienceProfileChartSlideV2({
        slideMaster,
        campaignAnalyticsReport: props.campaignAnalyticsReport,
      })
    : [];

const demographicProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.demographicProfile &&
  props.campaignAnalyticsReport
    ? [
        demographicProfileChartSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const stateAndDmaVisitsTableSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.stateAndDmaVisits &&
  props.campaignAnalyticsReport
    ? [
        stateAndDmaVisitsTableSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const brandLocationsTableSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.brandLocationVisits &&
  props.campaignAnalyticsReport
    ? [
        brandLocationsTableSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const proximityToHomeChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.proximityToHome &&
  props.campaignAnalyticsReport
    ? [
        proximityToHomeChartSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const timeToFirstVisitChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.timeToFirstVisitByDay &&
  props.campaignAnalyticsReport
    ? [
        timeToFirstVisitChartSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const visitsByTimeOfDayChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.visitsByHourOfDay &&
  props.campaignAnalyticsReport
    ? [
        visitsByTimeOfDayChartSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

const visitsByDayOfWeekChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.customFields.includedSlides.campaignAnalytics.visitsByDayOfWeek &&
  props.campaignAnalyticsReport
    ? [
        visitsByDayOfWeekChartSlideV2({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];

export const campaignAnalyticsSlidesV2 = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.campaignAnalyticsReport
    ? [
        campaignAnalyticsTitleSlideFromProps(props),
        audienceProfileChartSlideFromProps(props, slideMaster),
        demographicProfileChartSlideFromProps(props, slideMaster),
        stateAndDmaVisitsTableSlideFromProps(props, slideMaster),
        brandLocationsTableSlideFromProps(props, slideMaster),
        proximityToHomeChartSlideFromProps(props, slideMaster),
        timeToFirstVisitChartSlideFromProps(props, slideMaster),
        visitsByTimeOfDayChartSlideFromProps(props, slideMaster),
        visitsByDayOfWeekChartSlideFromProps(props, slideMaster),
      ].flat()
    : [];
};
